import { Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { ReleaseNote } from '@/config/releaseNotes';
import { ReleaseNotesList } from './ReleaseNotesList';

export interface WhatsNewPopoverContentProps {
  readonly releases: readonly ReleaseNote[];
  /** Scroll-container cap — a popover-only concern, kept here rather than
   *  on `ReleaseNotesList` so the "full page, no cap" shape WNB-5's
   *  `/updates` page needs is structurally impossible to get wrong: that
   *  page composes `ReleaseNotesList` directly and never sees this prop
   *  at all (Swarm arbitration, WNB-5 — adopted over a `showFooterLink`
   *  boolean on this component, which would have made this prop and that
   *  one independently-settable but actually-coupled, with no compiler
   *  enforcement that a caller passes both or neither). */
  readonly maxHeight?: number | string;
  /** Called when "View all updates" is clicked, before navigating — lets
   *  the parent (`WhatsNewRow`) close the popover. The popover is sidebar
   *  chrome that persists across route changes, so without this it would
   *  stay open, overlaying `/updates` (Swarm review, WNB-4: State
   *  Management + TypeScript Critic both caught this independently). */
  readonly onNavigate?: () => void;
}

/**
 * The popover-only chrome around `ReleaseNotesList`: a scroll cap and a
 * sticky "View all updates" footer. WNB-5's `/updates` page composes
 * `ReleaseNotesList` directly instead of this component — it needs
 * neither the cap nor a footer link to itself.
 */
export const WhatsNewPopoverContent = ({
  releases,
  maxHeight = 420,
  onNavigate,
}: WhatsNewPopoverContentProps): JSX.Element => {
  if (releases.length === 0) {
    return <ReleaseNotesList releases={releases} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          maxHeight,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        <ReleaseNotesList releases={releases} />
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          px: 2,
          py: 1.5,
          textAlign: 'center',
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Link
          component={RouterLink}
          to="/updates"
          onClick={onNavigate}
          underline="hover"
          variant="body2"
          sx={{ color: 'text.link', fontWeight: 600 }}
        >
          View all updates
        </Link>
      </Box>
    </Box>
  );
};
